<script setup lang="ts">
import { useBrowserStore } from '@apps/main/new-desktop/core/stores';

import { AppSlidePanel, AppLinkButton, AppButton } from '@libs/ui';
import type { IngredientId } from '@libs/trip-planner-interfaces';

import type { FerryFilterEvents, FerryFilters } from './FerryFilters.model';
import FerryFiltersLogic from './FerryFiltersLogic.vue';

interface FerryFiltersPanelProps {
  isOpen: boolean;
  headerText: string;
  data: FerryFilters | null;
  shouldUpdateOnClick?: boolean;
}
const props = defineProps<FerryFiltersPanelProps>();

interface FerryFiltersPanelEmits {
  (eventName: 'action', context: FerryFilterEvents): void;
  (eventName: 'apply-all-filters', context: FerryFilterEvents[]): void;
  (eventName: 'reset-all-filters', ingredientId: IngredientId): void;
  (eventName: 'close'): void;
}
const emit = defineEmits<FerryFiltersPanelEmits>();

const browserStore = useBrowserStore();

let filterEventsActions: FerryFilterEvents[] = [];

function triggerAction(action: FerryFilterEvents): void {
  emit('action', action);
}

function storeNewActions(actions: FerryFilterEvents[]): void {
  filterEventsActions = actions;
}

function closeFiltersPanel(): void {
  emit('close');
}

function applyAllFilters(): void {
  emit('apply-all-filters', filterEventsActions);
}

function resetAllFilters(): void {
  if (props?.data?.ingredientId) {
    filterEventsActions.length = 0;
    emit('reset-all-filters', props.data.ingredientId);
  }
}
</script>

<template>
  <AppSlidePanel
    :is-open="props.isOpen"
    direction="right"
    :width="!browserStore.isLargerThan1199px ? '100%' : '33.33%'"
    padding="0"
    should-show-close-button
    :close-button-aria-label="$t('a11y.close_panel')"
    @close="closeFiltersPanel"
    @confirm="props.shouldUpdateOnClick ? closeFiltersPanel : applyAllFilters"
  >
    <template #title>
      {{ props.headerText }}
    </template>

    <template #body>
      <div class="ferry-filters-panel-body">
        <FerryFiltersLogic
          v-if="props.data"
          :data="props.data"
          :should-update-on-click="props.shouldUpdateOnClick"
          @trigger-action="triggerAction"
          @new-actions="storeNewActions"
        />
      </div>
    </template>
    <template #footer>
      <div class="ferry-filters-panel-footer">
        <template v-if="props.shouldUpdateOnClick">
          <AppLinkButton
            :text="$t('filter.reset_filters')"
            size="m"
            @click="resetAllFilters"
          />
          <AppButton
            type="primary"
            state="enabled"
            size="medium"
            @click="applyAllFilters"
          >
            {{ $t('planner.done') }}
          </AppButton>
        </template>
        <template v-else>
          <AppButton
            type="primary"
            state="enabled"
            size="large"
            :wide="true"
            @click="closeFiltersPanel"
          >
            {{ $t('planner.show_results') }}
          </AppButton>
        </template>
      </div>
    </template>
  </AppSlidePanel>
</template>

<style scoped lang="scss">
.ferry-filters-panel-body {
  padding-left: var(--spacing-16);
}

.ferry-filters-panel-footer {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-16);
}
</style>
